import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS } from '../commonStateTaxData';
import { basicInfoQuestions, sharedExemptionQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Basic Info + Line 1 + Line 2 + Line 3 (Marital Status)
  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  // Line 2b
  const [personalExemption, setPersonalExemption] = useState(
    data.personal_exemption || '',
  );
  const [personalExemptionError, setPersonalExemptionError] = useState(false);

  // Line 4 - Dependents
  const [numDependents, setNumDependents] = useState(data.dependents || '');
  const [numDependentsError, setNumDependentsError] = useState(false);
  const [numDependentsValueError, setNumDependentsValueError] = useState(false);

  // Line 5 - Age and Blindness
  const [husbandAge65, setHusbandAge65] = useState(data.husband_age_65 || false);
  const [wifeAge65, setWifeAge65] = useState(data.wife_age_65 || false);
  const [selfAge65, setSelfAge65] = useState(data.single_age_65 || false);
  const [husbandBlind, setHusbandBlind] = useState(data.husband_blind || false);
  const [wifeBlind, setWifeBlind] = useState(data.wife_blind || false);
  const [selfBlind, setSelfBlind] = useState(data.single_blind || false);

  // Line 7 - Additional Withholding
  const exemptionsForm = useRef();
  const sharedExemptionQuestionsInitQ = getInitialQuestions(
    sharedExemptionQuestions,
  );
  const [sharedExemptionQs, setSharedExemptionQs] = useState(
    sharedExemptionQuestionsInitQ,
  );

  // Line 8 - Exemption from withholding
  const [serviceMember, setServiceMember] = useState(
    data.exemption_from_withholding || false,
  );

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
    </Flex>
  );

  const renderPersonalExemptions = (isReviewStep) => (
    <Flex column mb="12px">
      <S.QuestionLabel isRequired>
        The personal exemption for married individuals filing jointly is
        $12,000. Enter that part of $12,000 claimed by you in multiples of $500.
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex>
          <S.Input
            type="text"
            value={personalExemption || ''}
            onChange={(e) => setPersonalExemption(e.target.value)}
            disabled={isReviewStep}
            hasError={personalExemptionError}
            style={{ borderColor: personalExemptionError ? 'red' : '' }}
          />
        </Flex>
      </S.QuestionWrapper>
      {personalExemptionError && (
      <Flex justifyContent="flex-start">
        <Text style={{ marginBottom: '10px' }} color="red">
          Please enter a value, less than or equal to $12,000, that is a
          multiple of $500.
        </Text>
      </Flex>
      )}
    </Flex>
  );

  const renderClaimExemptionsForm = (isReviewStep) => (
    <Flex column>
      {basicInfo[6].answer.answer.marital_status === 'married_spouse_employed'
        ? renderPersonalExemptions(isReviewStep)
        : ''}
      <Flex column mb="12px">
        <S.QuestionLabel>
          You may claim $1,500 for each dependent, other than for taxpayer and
          spouse, who receives the chief support from you and who qualifies as a
          dependent for federal income tax purposes. Enter the number of
          dependents claimed. (Line 4)
        </S.QuestionLabel>
        <S.QuestionWrapper>
          <Flex>
            <S.Input
              type="text"
              value={numDependents || ''}
              onChange={(e) => setNumDependents(e.target.value)}
              disabled={isReviewStep}
              style={{
                borderColor:
                    numDependentsError || numDependentsValueError ? 'red' : '',
              }}
            />
          </Flex>
        </S.QuestionWrapper>
        {numDependentsError && (
        <Flex justifyContent="flex-start" mt="10px" mb="5px">
          <Text color="red">
            To qualify as head of family, you must be single and have at least
            one dependent living in the home with you.
          </Text>
        </Flex>
        )}
        {numDependentsValueError && (
        <Flex justifyContent="flex-start" mt="10px" mb="5px">
          <Text color="red">Please enter a valid response</Text>
        </Flex>
        )}
      </Flex>
      <S.QuestionLabel mb="7px">Age and blindness (Line 5)</S.QuestionLabel>
      <Text>
        Additional exemptions of $1,500 may be claimed by either taxpayer or
        spouse or both if either or both have reached the age of 65 before the
        close of the taxable year, or either taxpayer or spouse or both if
        either or both are blind. Select the applicable blocks for the
        exemptions you want to claim.
      </Text>
      <S.QuestionLabel mb="7px">Age 65 or older</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={husbandAge65}
              checked={husbandAge65}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setHusbandAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Husband
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={wifeAge65}
              checked={wifeAge65}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setWifeAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Wife
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={selfAge65}
              checked={selfAge65}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setSelfAge65(e.target.checked);
              }}
            />
            <Box ml={2} />
            Single
          </Flex>
        </label>
      </S.QuestionWrapper>
      <S.QuestionLabel mb="7px">Blind</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={husbandBlind}
              checked={husbandBlind}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setHusbandBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Husband
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={wifeBlind}
              checked={wifeBlind}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setWifeBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Wife
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={selfBlind}
              checked={selfBlind}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setSelfBlind(e.target.checked);
              }}
            />
            <Box ml={2} />
            Single
          </Flex>
        </label>
      </S.QuestionWrapper>
      <FormConsumer
        questions={sharedExemptionQs}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = sharedExemptionQs.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setSharedExemptionQs(questions);
        }}
        ref={exemptionsForm}
      />
      <S.QuestionLabel mb="7px">Military Spouses Residency Relief Act Exemption from Mississippi Withholding (Line 8)</S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={serviceMember}
              checked={serviceMember}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setServiceMember(e.target.checked);
              }}
            />
            <Box ml={2} />
            Check this box if you meet the conditions set forth under the
            Service Member Civil Relief, as amended by the Military Spouses
            Residency Relief Act, and have no Mississippi tax liability.
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const calculateTotalExemptions = () => {
    const ms = basicInfo[6].answer.answer.marital_status;
    let personalExemptionAmount;
    if (ms === 'single') {
      personalExemptionAmount = 6000;
    } else if (ms === 'married_spouse_unemployed') {
      personalExemptionAmount = 12000;
    } else if (ms === 'married_spouse_employed') {
      personalExemptionAmount = personalExemption;
    } else {
      personalExemptionAmount = 9500;
    }

    const dependentsAmt = numDependents || 0;
    const row4 = parseInt(dependentsAmt, 10) * 1500;

    let numBoxesChecked = 0;
    if (husbandAge65) numBoxesChecked += 1;
    if (wifeAge65) numBoxesChecked += 1;
    if (selfAge65) numBoxesChecked += 1;
    if (husbandBlind) numBoxesChecked += 1;
    if (wifeBlind) numBoxesChecked += 1;
    if (selfBlind) numBoxesChecked += 1;

    const row5 = numBoxesChecked * 1500;

    return parseInt(personalExemptionAmount, 10) + row4 + row5;
  };

  const handleValidation = () => {
    if (basicInfoForm && basicInfoForm.current
      && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    // Additional Withholding Questions
    if (exemptionsForm && exemptionsForm.current
      && exemptionsForm.current.validate((error) => !!error)) {
      return true;
    }

    const maritalStatus = basicInfo[6].answer.answer.marital_status;

    // If marital status - spouse is employed
    // - personal exemption should be multiple of 500 and less than 12000
    if (maritalStatus === 'married_spouse_employed') {
      if (personalExemption === ''
        || personalExemption % 500 !== 0
        || personalExemption > 12000) {
        setPersonalExemptionError(true);
        return true;
      }
      setPersonalExemptionError(false);
    }

    // If marital status - head of family
    // - numDependents should not be 0
    if (maritalStatus === 'head_of_family') {
      if (numDependents === '' || numDependents === '0') {
        setNumDependentsError(true);
        return true;
      }
    }
    setNumDependentsError(false);

    if (!/^\d*$/.test(numDependents)) {
      setNumDependentsValueError(true);
      return true;
    }
    setNumDependentsValueError(false);

    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();
    if (hasErrors) return;

    const basicInfoData = basicInfo.reduce(
      (acc, question) => ({ ...acc, ...question.answer.answer }),
      {},
    );

    let personalExemptionData = '';
    if (basicInfo[6].answer.answer.marital_status === 'married_spouse_employed') {
      personalExemptionData = personalExemption;
    }

    const sharedExemptionData = sharedExemptionQs.reduce(
      (acc, question) => ({ ...acc, ...question.answer.answer }),
      {},
    );
    const serviceMemberData = {
      exemption_from_withholding: serviceMember,
    };
    const dependentsData = { dependents: numDependents };
    const totalExemptionsTemp = calculateTotalExemptions();

    const exemptionCalculations = {
      total_exemptions: totalExemptionsTemp,
    };
    const additionalExemptionData = {
      husband_age_65: husbandAge65,
      wife_age_65: wifeAge65,
      single_age_65: selfAge65,
      husband_blind: husbandBlind,
      wife_blind: wifeBlind,
      single_blind: selfBlind,
    };
    const data = {
      personal_exemption: personalExemptionData,
      ...basicInfoData,
      ...additionalExemptionData,
      ...personalExemptionData,
      ...sharedExemptionData,
      ...dependentsData,
      ...exemptionCalculations,
      ...serviceMemberData,
    };

    saveFormData(data, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderClaimExemptionsForm()}
        </Flex>
        {viewResultAsManager
            && companyStaff.pii_permission === 'full_access' && (
              <Flex column mt={10} mb={10}>
                <Flex alignItems="center">
                  <Text mr={16}>Download signed documents</Text>
                  <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                    <img src="/j/images/onboarding/download.svg" />
                  </a>
                </Flex>
              </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
        <Flex justifyContent="flex-end">
          <ActionNote text={disabledText} />
        </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderClaimExemptionsForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
